import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  getProjectStudies,
  getAccountStudies,
  createStudy,
  getStudy,
  updateStudy,
  deleteStudy,
  GetStudiesReq,
} from "./api";
import { toast } from "@/components/ui/toast";

/**
 * useGetProjectStudies Query
 *
 * Get all studies for a given projectId.
 *
 * queryKey: ["studies", projectId, params]
 *
 * @param projectId - The project ID to fetch studies for
 * @param params - Optional filtering parameters
 */
export const useGetProjectStudies = (projectId: string, params: GetStudiesReq) => {
  return useQuery({
    queryKey: ["studies", projectId, params],
    queryFn: () => getProjectStudies(projectId, params),
  });
};

/**
 * useGetAccountStudies Query
 *
 * Get all studies for a given account.
 *
 * queryKey: ["studies", params]
 *
 * @param params - Optional filtering parameters
 */
export const useGetAccountStudies = (params: GetStudiesReq) => {
  return useQuery({
    queryKey: ["studies", params],
    queryFn: () => getAccountStudies(params),
  });
};

/**
 * useCreateStudy Mutation
 *
 * Create a new study against a project.
 *
 * We invalidate all studies queries due to the mix of account level and project level queries.
 */
export const useCreateStudy = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: createStudy,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["studies"] });
      toast({
        title: "Study successfully created",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to create study",
        type: "error",
      });
    },
  });
};

/**
 * useGetStudy Query
 *
 * Get a study from a project by its id.
 *
 * queryKey: ["studies", studyId]
 *
 * @param projectId - The project ID
 * @param studyId - The study ID to fetch
 */
export const useGetStudy = (projectId: string, studyId: string) => {
  return useQuery({
    queryKey: ["studies", studyId],
    queryFn: () => getStudy(projectId, studyId),
    select: (data) => data?.study,
  });
};

/**
 * useUpdateStudy Mutation
 *
 * Update study details.
 *
 * We invalidate all studies queries due to the mix of account level and project level queries.
 */
export const useUpdateStudy = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: updateStudy,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["studies"] });
      if (vars.backgroundUpdate) return;
      toast({
        title: "Study successfully updated",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update study",
        type: "error",
      });
    },
  });
};

/**
 * useArchiveStudy Mutation
 *
 * It's the same as useUpdateStudy but with a different onSuccess message.
 */
export const useArchiveStudy = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: updateStudy,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["studies"] });
      if (vars.backgroundUpdate) return;
      toast({
        title: vars.values.isArchived ? "Study successfully archived" : "Study successfully unarchived",
        type: "success",
      });
    },
    onError: (_, vars) => {
      toast({
        title: vars.values.isArchived ? "Failed to archived study" : "Failed to unarchived study",
        type: "error",
      });
    },
  });
};

/**
 * useDeleteStudy Mutation
 *
 * Delete a study from a project by its id.
 *
 * We invalidate all studies queries due to the mix of account level and project level queries.
 */
export const useDeleteStudy = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: deleteStudy,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["studies"] });
      toast({
        title: "Study successfully deleted",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to delete study",
        type: "error",
      });
    },
  });
};
